import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MdEdit, MdDelete } from 'react-icons/md';
import AdminService from "../../Services/AdminService";
import AdminData from "../../Models/AdminData";
import ConfirmDialog from "../../Components/ConfirmDialog/ConfirmDialog";
import AddCompanyModal from "../../Components/AddCompanyModal/AddCompanyModal";
import CreateCompanyDto from "../../Models/CreateStoreDto";
import Company, { EntityTypes } from "../../Models/Store";
import Pagination from "../../Components/Pagination/Pagination";
import SearchBox from "../../Components/SearchBox/SearchBox";
import Loader from "../../Components/Loader/Loader";
import TokenExpiredPopUp from "../../Components/TokenExpiredPopUp/TokenExpiredPopUp";
import CreateStoreDto from "../../Models/CreateStoreDto";
import "./AdminStoresView.css";

const AdminStoresView: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [tokenInvalide, setTokenInvalide] = useState<boolean>(false);

    const [adminData, setAdminData] = useState<AdminData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // companies state
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [editingCompany, setEditingCompany] = useState<Company | null>(null);
    const [savingCompany, setSavingCompany] = useState(false);
    const [confirmDeleteCompany, setConfirmDeleteCompany] = useState<Company | null>(null);
    const [deletingCompanyId, setDeletingCompanyId] = useState<string | null>(null);
    const [companyError, setCompanyError] = useState<string | null>(null);

    // pagination state
    const PAGE_SIZE = 7;
    const [companiesPage, setCompaniesPage] = useState<number>(1);

    const [companies, setCompanies] = useState<Company[]>([]);
    const [companiesRaw, setCompaniesRaw] = useState<Company[]>([]);
    const [companiesQuery, setCompaniesQuery] = useState<string>("");
    const [companiesTotal, setCompaniesTotal] = useState<number>(0);

    // load admin data
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await AdminService.getAdminData(token);
            setAdminData(data);
        } catch (err: any) {
            if (err.status === 401) {
                setTokenInvalide(true);
            } else {
                setError("Une erreur inconnue est survenue. Veuillez réessayer plus tard ou contacter votre service informatique.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [token]);

    // fetch paged lists with fallback to adminData
    const fetchCompanies = async (page: number) => {
        try {
            if (adminData) {
                const base = adminData.stores || [];
                const filtered = applyFilter(base, companiesQuery);
                const start = (page - 1) * PAGE_SIZE;
                const slice = filtered.slice(start, start + PAGE_SIZE);
                setCompaniesRaw(slice);
                setCompanies(slice);
                setCompaniesTotal(filtered.length);
                return;
            }

            const res = await AdminService.getStoresPaged(token, page, PAGE_SIZE);
            const normalized = (res.items || []);
            setCompaniesRaw(normalized);
            setCompanies(applyFilter(normalized, companiesQuery));
            setCompaniesTotal(res.totalItems);
        } catch (e) {
            setCompanies([]);
            setCompaniesRaw([]);
            setCompaniesTotal(0);
        }
    };

    useEffect(() => { if (token) fetchCompanies(companiesPage); }, [token, companiesPage, companiesQuery, adminData]);
    // suppression du chargement de la table entités côté front

    // reset pages when data changes
    useEffect(() => {
        setCompaniesPage(1);
    }, [adminData]);

    const applyFilter = <T extends { name: string, codes: string[] }>(items: T[], query: string) => {
        const q = (query || "").trim().toLowerCase();
        if (!q) return items;
        return items.filter(x => x.name.trim().toLowerCase().includes(q) || x.codes.some(c => c.trim().toLowerCase().includes(q)));
    };

    useEffect(() => {
        setCompanies(applyFilter(companiesRaw, companiesQuery));
    }, [companiesQuery, companiesRaw]);
    
    if (loading) return <Loader label = "Chargement..."/>;

    return (
        <div id="AdminStore-View" className="admin-dashboard">
            { tokenInvalide && <TokenExpiredPopUp/>}
            {error ? (
                <p className="Error">{error}</p>
            ) : (
                <div className="dashboard-content">

                    {/* COMPANIES */}
                    <div className="company-management">
                        <h2>Gérer les Magasins</h2>
                        <div className="section-header">
                            <button className="btn" onClick={() => setShowCompanyModal(true)}>
                                + Ajouter un Magasin
                            </button>
                            <SearchBox onSearch={setCompaniesQuery} className="section-search" searchQuery={companiesQuery} />
                        </div>
                        {companyError && <p className="Error">{companyError}</p>}
                        <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Code</th>
                                    <th>Entités</th>
                                    <th className="table-actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map((company) => (
                                    <tr key={company.id}>
                                        <td>{company.name}</td>
                                        <td>{company.codes.join(" | ")}</td>
                                        <td>
                                            <div className="tables-entity-items">
                                                {company.entities.map((st: EntityTypes, index: number) => (
                                                    <div className="Entities" key={index}>{EntityTypes[st]}</div>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="table-actions">
                                            <button
                                                className="btn-edit"
                                                onClick={() => {
                                                    setCompanyError(null);
                                                    setEditingCompany(company);
                                                }}
                                            >
                                                <MdEdit className="btn-edit-icone" />
                                            </button>
                                            <button
                                                className="btn-icon btn-danger"
                                                title="Supprimer"
                                                disabled={deletingCompanyId === company.id}
                                                onClick={() => {
                                                    setCompanyError(null);
                                                    setConfirmDeleteCompany(company);
                                                }}
                                            >
                                                <MdDelete className="btn-delete-icone" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        {(
                            <div className="pagination">
                                <Pagination
                                    currentPage={companiesPage}
                                    totalItems={companiesTotal}
                                    pageSize={PAGE_SIZE}
                                    onChange={setCompaniesPage}
                                />
                            </div>
                        )}
                    </div>

                    {/* COMPANY CREATE */}
                    {showCompanyModal && adminData && (
                        <AddCompanyModal
                            onClose={() => setShowCompanyModal(false)}
                            onSave={async (data: { name: string; codes: string[]; selectedEntityTypes: EntityTypes[] }) => {
                                setCompanyError(null);
                                setSavingCompany(true);
                                try {
                                    await AdminService.createStore(token, { name: data.name, codes: data.codes, entities: data.selectedEntityTypes } as CreateStoreDto);
                                    await fetchData();
                                    setShowCompanyModal(false);
                                } catch (err: any) {
                                    let msg = "Impossible de créer le magasin. Veuillez réessayer.";
                                    if (err?.status === 400) {
                                        try {
                                            const json = JSON.parse(err.message);
                                            if (json?.message) msg = json.message;
                                        } catch {}
                                    } else if (err?.status === 409) {
                                        try {
                                            const json = JSON.parse(err.message);
                                            if (json?.message) msg = json.message;
                                        } catch {}
                                    } else if (err?.status === 404) {
                                        msg = "Le magasin demandé est introuvable.";
                                    }
                                    setCompanyError(msg);
                                    throw err;
                                } finally {
                                    setSavingCompany(false);
                                }
                            }}
                        />
                    )}

                    {/* COMPANY EDIT */}
                    {editingCompany && adminData && (
                        <AddCompanyModal
                            mode="edit"
                            initialValues={{
                                name: editingCompany.name,
                                codes: editingCompany.codes,
                                selectedEntityTypes: editingCompany.entities,
                            }}
                            onClose={() => setEditingCompany(null)}
                            submitLabelOverride={savingCompany ? "Mise à jour..." : "Mettre à jour"}
                            onSave={async (data: { name: string; codes: string[]; selectedEntityTypes: EntityTypes[] }) => {
                                setCompanyError(null);
                                setSavingCompany(true);
                                try {
                                    await AdminService.updateStore(token, editingCompany.id, { name: data.name, codes: data.codes, entities: data.selectedEntityTypes } as CreateCompanyDto);
                                    await fetchData();
                                    setEditingCompany(null);
                                } catch (err: any) {
                                    let msg = "Impossible de mettre à jour le magasin. Veuillez réessayer.";
                                    if (err?.status === 400) {
                                        try {
                                            const json = JSON.parse(err.message);
                                            if (json?.message) msg = json.message;
                                        } catch {}
                                    } else if (err?.status === 409) {
                                        try {
                                            const json = JSON.parse(err.message);
                                            if (json?.message) msg = json.message;
                                        } catch {}
                                    } else if (err?.status === 404) {
                                        msg = "Le magasin demandé est introuvable.";
                                    }
                                    setCompanyError(msg);
                                    throw err;
                                } finally {
                                    setSavingCompany(false);
                                }
                            }}
                        />
                    )}

                    {/* COMPANY DELETE */}
                    <ConfirmDialog
                        open={!!confirmDeleteCompany}
                        title="Supprimer le magasin"
                        message={
                            confirmDeleteCompany ? (
                                <>Voulez-vous vraiment supprimer <b>{confirmDeleteCompany.name}</b> ? Cette action est irréversible.</>
                            ) : null
                        }
                        confirmText="Supprimer"
                        cancelText="Annuler"
                        danger
                        loading={!!deletingCompanyId}
                        onCancel={() => setConfirmDeleteCompany(null)}
                        onConfirm={async () => {
                            if (!confirmDeleteCompany) return;
                            try {
                                setDeletingCompanyId(confirmDeleteCompany.id);
                                await AdminService.deleteStore(token, confirmDeleteCompany.id);
                                await fetchData();
                                setConfirmDeleteCompany(null);
                            } catch {
                                setCompanyError("Impossible de supprimer le magasin. Réessayez.");
                            } finally {
                                setDeletingCompanyId(null);
                            }
                        }}
                    />

                </div>
            )}
        </div>
    );
};

export default AdminStoresView;
